import {
  type PartyFetchOptions,
  PartySocket,
  type PartySocketOptions
} from "partysocket";
import type { Agent, RPCRequest, RPCResponse } from "./";
import type {
  ClientParameters,
  Method,
  RPCMethod,
  SerializableReturnValue,
  SerializableValue
} from "./serializable";
import { MessageType } from "./types";
import { camelCaseToKebabCase, isInternalJsStubProp } from "./utils";

export class AgentConnectionError extends Error {
  code: number;
  reason: string;
  wasClean: boolean;

  constructor(event: CloseEvent) {
    const reason = event.reason || `WebSocket closed with code ${event.code}`;
    super(`Agent connection closed: ${reason}`);
    this.name = "AgentConnectionError";
    this.code = event.code;
    this.reason = event.reason;
    this.wasClean = event.wasClean;
  }
}

export function isTerminalCloseEvent(event: CloseEvent): boolean {
  return event.code === 1008 || (event.code >= 4000 && event.code <= 4999);
}

type TerminalReconnectOptions = {
  shouldReconnectOnClose?: (event: CloseEvent) => boolean;
};

/**
 * Options for creating an AgentClient
 */
export type AgentClientOptions<State = unknown> = Omit<
  PartySocketOptions,
  "party" | "room"
> &
  TerminalReconnectOptions & {
    /** Name of the agent to connect to (ignored if basePath is set) */
    agent: string;
    /** Name of the specific Agent instance (ignored if basePath is set) */
    name?: string;
    /**
     * Full URL path - bypasses agent/name URL construction.
     * When set, the client connects to this path directly.
     * Server must handle routing manually (e.g., with getAgentByName + fetch).
     * @example
     * // Client connects to /user, server routes based on session
     * useAgent({ agent: "UserAgent", basePath: "user" })
     */
    basePath?: string;
    /** Called when the Agent's state is updated */
    onStateUpdate?: (state: State, source: "server" | "client") => void;
    /** Called when a state update fails (e.g., connection is readonly) */
    onStateUpdateError?: (error: string) => void;
    /**
     * Called when the server sends the agent's identity on connect.
     * Useful when using basePath, as the actual instance name is determined server-side.
     * @param name The actual agent instance name
     * @param agent The agent class name (kebab-case)
     */
    onIdentity?: (name: string, agent: string) => void;
    /**
     * Called when identity changes on reconnect (different instance than before).
     * If not provided and identity changes, a warning will be logged.
     * @param oldName Previous instance name
     * @param newName New instance name
     * @param oldAgent Previous agent class name
     * @param newAgent New agent class name
     */
    onIdentityChange?: (
      oldName: string,
      newName: string,
      oldAgent: string,
      newAgent: string
    ) => void;
    /**
     * Additional path to append to the URL.
     * Works with both standard routing and basePath.
     * @example
     * // With basePath: /user/settings
     * { basePath: "user", path: "settings" }
     * // Standard: /agents/my-agent/room/settings
     * { agent: "MyAgent", name: "room", path: "settings" }
     */
    path?: string;
    /**
     * Default timeout (in milliseconds) applied to non-streaming `call()`s
     * that don't pass an explicit `timeout`. Defaults to 30 000 ms.
     * Set to `0` to disable. Streaming calls never get a default timeout.
     */
    defaultCallTimeout?: number;
    /** Called when the connection closes with a terminal code and will not reconnect. */
    onConnectionError?: (error: AgentConnectionError) => void;
  };

/**
 * Default timeout (in milliseconds) applied to non-streaming RPC calls
 * that don't pass an explicit `timeout`. Acts as a backstop so calls
 * whose response is lost (e.g. the connection drops mid-flight) reject
 * instead of hanging forever. Override per client via
 * `defaultCallTimeout`, or per call via `timeout` (0 disables).
 */
export const DEFAULT_CALL_TIMEOUT_MS = 30_000;

/**
 * Options for streaming RPC calls
 */
export type StreamOptions = {
  /** Called when a chunk of data is received */
  onChunk?: (chunk: unknown) => void;
  /** Called when the stream ends */
  onDone?: (finalChunk: unknown) => void;
  /** Called when an error occurs */
  onError?: (error: string) => void;
};

/**
 * Options for RPC calls
 */
export type CallOptions = {
  /** Timeout in milliseconds. If the call doesn't complete within this time, it will be rejected. */
  timeout?: number;
  /** Streaming options for handling streaming responses */
  stream?: StreamOptions;
};

/**
 * Options for the agentFetch function
 */
export type AgentClientFetchOptions = Omit<
  PartyFetchOptions,
  "party" | "room"
> & {
  /** Name of the agent to connect to (ignored if basePath is set) */
  agent: string;
  /** Name of the specific Agent instance (ignored if basePath is set) */
  name?: string;
  /**
   * Full URL path - bypasses agent/name URL construction.
   * When set, the request is made to this path directly.
   */
  basePath?: string;
};

// ---- Shared RPC Type Utilities ----

type AllOptional<T> = T extends [infer A, ...infer R]
  ? undefined extends A
    ? AllOptional<R>
    : false
  : true;

export type RPCMethods<T> = {
  [K in keyof T as T[K] extends RPCMethod<T[K]> ? K : never]: RPCMethod<T[K]>;
};

type OptionalParametersMethod<T extends RPCMethod> =
  AllOptional<ClientParameters<T>> extends true ? T : never;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any -- generic agent type constraint
export type AgentMethods<T> = Omit<RPCMethods<T>, keyof Agent<any, any>>;

export type OptionalAgentMethods<T> = {
  [K in keyof AgentMethods<T> as AgentMethods<T>[K] extends OptionalParametersMethod<
    AgentMethods<T>[K]
  >
    ? K
    : never]: OptionalParametersMethod<AgentMethods<T>[K]>;
};

export type RequiredAgentMethods<T> = Omit<
  AgentMethods<T>,
  keyof OptionalAgentMethods<T>
>;

export type AgentPromiseReturnType<T, K extends keyof AgentMethods<T>> =
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any -- generic promise return type
  ReturnType<AgentMethods<T>[K]> extends Promise<any>
    ? ReturnType<AgentMethods<T>[K]>
    : Promise<ReturnType<AgentMethods<T>[K]>>;

export type AgentStub<T> = {
  [K in keyof AgentMethods<T>]: (
    ...args: ClientParameters<AgentMethods<T>[K]>
  ) => AgentPromiseReturnType<AgentMethods<T>, K>;
};

export type UntypedAgentStub = Record<string, Method>;

type AgentClientStub<AgentT> = keyof AgentMethods<AgentT> extends never
  ? UntypedAgentStub
  : AgentStub<AgentT>;

type OptionalArgsAgentClientCall<AgentT> = <
  K extends keyof OptionalAgentMethods<AgentT>
>(
  method: K,
  args?: ClientParameters<OptionalAgentMethods<AgentT>[K]>,
  options?: CallOptions | StreamOptions
) => AgentPromiseReturnType<AgentT, K>;

type RequiredArgsAgentClientCall<AgentT> = <
  K extends keyof RequiredAgentMethods<AgentT>
>(
  method: K,
  args: ClientParameters<RequiredAgentMethods<AgentT>[K]>,
  options?: CallOptions | StreamOptions
) => AgentPromiseReturnType<AgentT, K>;

type TypedAgentClientCall<AgentT> = OptionalArgsAgentClientCall<AgentT> &
  RequiredArgsAgentClientCall<AgentT>;

type UntypedAgentClientCall = {
  <T extends SerializableReturnValue>(
    method: string,
    args?: SerializableValue[],
    options?: CallOptions | StreamOptions
  ): Promise<T>;
  <T = unknown>(
    method: string,
    args?: unknown[],
    options?: CallOptions | StreamOptions
  ): Promise<T>;
};

type AgentClientCall<AgentT> = keyof AgentMethods<AgentT> extends never
  ? UntypedAgentClientCall
  : TypedAgentClientCall<AgentT>;

/**
 * Creates a proxy that wraps RPC method calls.
 * Internal JS methods (toJSON, then, etc.) return undefined to avoid
 * triggering RPC calls during serialization (e.g., console.log)
 */
export function createStubProxy<T = Record<string, Method>>(
  call: (method: string, args: unknown[]) => unknown
): T {
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any -- proxy needs any for dynamic method access
  return new Proxy<any>(
    {},
    {
      get: (_target, method) => {
        if (isInternalJsStubProp(method)) {
          return undefined;
        }
        return (...args: unknown[]) => call(method as string, args);
      }
    }
  );
}

/**
 * WebSocket client for connecting to an Agent
 */
export class AgentClient<
  AgentT = unknown,
  State = AgentT extends { get state(): infer S } ? S : AgentT
> extends PartySocket {
  /**
   * @deprecated Use agentFetch instead
   */
  static fetch(_opts: PartyFetchOptions): Promise<Response> {
    throw new Error(
      "AgentClient.fetch is not implemented, use agentFetch instead"
    );
  }
  agent: string;
  name: string;
  call: AgentClientCall<AgentT>;
  stub: AgentClientStub<AgentT>;

  /**
   * The current agent state, updated on server broadcasts and client setState calls.
   * Starts as undefined until the first state message is received from the server.
   */
  state: State | undefined = undefined;

  /**
   * Whether the client has received identity from the server.
   * Becomes true after the first identity message is received.
   * Resets to false on connection close.
   */
  identified = false;

  /**
   * Terminal connection error, if the server closed the socket with a code
   * that should not be retried automatically.
   */
  connectionError: AgentConnectionError | null = null;

  /**
   * Promise that resolves when identity has been received from the server.
   * Useful for waiting before making calls that depend on knowing the instance.
   * Resets on connection close so it can be awaited again after reconnect.
   */
  get ready(): Promise<void> {
    return this._readyPromise;
  }

  private options: AgentClientOptions<State>;
  private _pendingCalls = new Map<
    string,
    {
      resolve: (value: unknown) => void;
      reject: (error: Error) => void;
      stream?: StreamOptions;
      /**
       * Whether the request was actually transmitted to the server.
       * Requests issued while disconnected sit in PartySocket's internal
       * buffer (transmitted: false) and are flushed on the next open —
       * they survive a transient close instead of being rejected, since
       * the server never saw them and re-delivery can't double-execute.
       */
      transmitted: boolean;
    }
  >();
  private _readyPromise!: Promise<void>;
  private _resolveReady!: () => void;
  private _previousName: string | null = null;
  private _previousAgent: string | null = null;

  private _resetReady() {
    this._readyPromise = new Promise((resolve) => {
      this._resolveReady = resolve;
    });
  }

  constructor(options: AgentClientOptions<State>) {
    const agentNamespace = camelCaseToKebabCase(options.agent);
    const shouldReconnectOnClose = options.shouldReconnectOnClose;
    const classifyReconnect = (event: CloseEvent) =>
      (shouldReconnectOnClose?.(event) ?? true) && !isTerminalCloseEvent(event);

    // If basePath is provided, use it directly; otherwise construct from agent/name
    const socketOptions = options.basePath
      ? {
          basePath: options.basePath,
          path: options.path,
          ...options,
          shouldReconnectOnClose: classifyReconnect
        }
      : {
          party: agentNamespace,
          prefix: "agents",
          room: options.name || "default",
          path: options.path,
          ...options,
          shouldReconnectOnClose: classifyReconnect
        };

    super(socketOptions);
    this.agent = agentNamespace;
    this.name = options.name || "default";
    this.options = options;

    // Initialize ready promise
    this._resetReady();

    this.addEventListener("message", (event) => {
      if (typeof event.data === "string") {
        let parsedMessage: Record<string, unknown>;
        try {
          parsedMessage = JSON.parse(event.data);
        } catch (_error) {
          // silently ignore invalid messages for now
          // TODO: log errors with log levels
          return;
        }
        if (parsedMessage.type === MessageType.CF_AGENT_IDENTITY) {
          const oldName = this._previousName;
          const oldAgent = this._previousAgent;
          const newName = parsedMessage.name as string;
          const newAgent = parsedMessage.agent as string;

          // Resolve ready/identified
          this.identified = true;
          this._resolveReady();

          // Detect identity change on reconnect
          if (
            oldName !== null &&
            oldAgent !== null &&
            (oldName !== newName || oldAgent !== newAgent)
          ) {
            if (this.options.onIdentityChange) {
              this.options.onIdentityChange(
                oldName,
                newName,
                oldAgent,
                newAgent
              );
            } else {
              const agentChanged = oldAgent !== newAgent;
              const nameChanged = oldName !== newName;
              let changeDescription = "";
              if (agentChanged && nameChanged) {
                changeDescription = `agent "${oldAgent}" → "${newAgent}", instance "${oldName}" → "${newName}"`;
              } else if (agentChanged) {
                changeDescription = `agent "${oldAgent}" → "${newAgent}"`;
              } else {
                changeDescription = `instance "${oldName}" → "${newName}"`;
              }
              console.warn(
                `[agents] Identity changed on reconnect: ${changeDescription}. ` +
                  "This can happen with server-side routing (e.g., basePath with getAgentByName) " +
                  "where the instance is determined by auth/session. " +
                  "Provide onIdentityChange callback to handle this explicitly, " +
                  "or ignore if this is expected for your routing pattern."
              );
            }
          }

          // Always update from server identity (server is authoritative)
          this._previousName = newName;
          this._previousAgent = newAgent;
          this.name = newName;
          this.agent = newAgent;

          // Call onIdentity callback
          this.options.onIdentity?.(newName, newAgent);
          return;
        }
        if (parsedMessage.type === MessageType.CF_AGENT_STATE) {
          this.state = parsedMessage.state as State;
          this.options.onStateUpdate?.(parsedMessage.state as State, "server");
          return;
        }
        if (parsedMessage.type === MessageType.CF_AGENT_STATE_ERROR) {
          this.options.onStateUpdateError?.(parsedMessage.error as string);
          return;
        }
        if (parsedMessage.type === MessageType.RPC) {
          const response = parsedMessage as RPCResponse;
          const pending = this._pendingCalls.get(response.id);
          if (!pending) {
            console.warn(
              `[AgentClient] Discarded an RPC response with no matching pending call (id "${response.id}"). ` +
                "The call likely timed out or was rejected when its connection closed before the response arrived."
            );
            return;
          }

          if (!response.success) {
            pending.reject(new Error(response.error));
            this._pendingCalls.delete(response.id);
            pending.stream?.onError?.(response.error);
            return;
          }

          // Handle streaming responses
          if ("done" in response) {
            if (response.done) {
              pending.resolve(response.result);
              this._pendingCalls.delete(response.id);
              pending.stream?.onDone?.(response.result);
            } else {
              pending.stream?.onChunk?.(response.result);
            }
          } else {
            // Non-streaming response
            pending.resolve(response.result);
            this._pendingCalls.delete(response.id);
          }
        }
      }
    });

    // PartySocket flushes its internal message buffer right before
    // dispatching "open" — anything queued has now been transmitted.
    this.addEventListener("open", () => {
      this.connectionError = null;
      for (const pending of this._pendingCalls.values()) {
        pending.transmitted = true;
      }
    });

    // Clean up pending calls and reset ready state when connection closes
    this.addEventListener("close", (event) => {
      const terminalClose = isTerminalCloseEvent(event);
      // Reset ready state for next connection
      this.identified = false;
      this._resetReady();

      if (this.shouldReconnect) {
        // Transient disconnect: reject calls whose request was already
        // transmitted — their response can never arrive. Buffered calls
        // stay pending; PartySocket re-sends them on reconnect.
        this._rejectPendingCalls("Connection closed", {
          onlyTransmitted: true
        });
      } else {
        // Permanent close (close() called or retries exhausted): nothing
        // will ever flush the buffer, so reject everything.
        this._rejectPendingCalls("Connection closed");
        if (terminalClose) {
          const error = new AgentConnectionError(event);
          this.connectionError = error;
          this.options.onConnectionError?.(error);
        }
      }
    });

    this.call = this._callImpl.bind(this) as AgentClientCall<AgentT>;
    this.stub = createStubProxy((method, args) =>
      this._callImpl(method, args)
    ) as AgentClientStub<AgentT>;
  }

  /**
   * Reject pending RPC calls with the given reason.
   * With `onlyTransmitted`, calls still sitting in the send buffer are
   * kept pending (they'll be flushed on reconnect).
   */
  private _rejectPendingCalls(
    reason: string,
    { onlyTransmitted = false }: { onlyTransmitted?: boolean } = {}
  ) {
    const error = new Error(reason);
    for (const [id, pending] of this._pendingCalls) {
      if (onlyTransmitted && !pending.transmitted) continue;
      this._pendingCalls.delete(id);
      pending.reject(error);
      pending.stream?.onError?.(reason);
    }
  }

  setState(state: State) {
    this.send(JSON.stringify({ state, type: MessageType.CF_AGENT_STATE }));
    this.state = state;
    this.options.onStateUpdate?.(state, "client");
  }

  /**
   * Close the connection and immediately reject all pending RPC calls.
   * This provides immediate feedback on intentional close rather than
   * waiting for the WebSocket close handshake to complete.
   *
   * Note: Any calls made after `close()` will be rejected when the
   * underlying WebSocket close event fires.
   */
  close(code?: number, reason?: string) {
    // Immediately reject all pending calls on intentional close
    this._rejectPendingCalls("Connection closed");

    // Then close the underlying socket
    super.close(code, reason);
  }

  /**
   * Call a method on the Agent.
   * When AgentT is provided, method names are inferred from the agent's methods.
   * Falls back to untyped string-based calls when AgentT is not provided.
   */
  private async _callImpl(
    method: string,
    args: unknown[] = [],
    options?: CallOptions | StreamOptions
  ): Promise<unknown> {
    if (this.connectionError && this.readyState === this.CLOSED) {
      throw new Error("Connection closed");
    }

    return new Promise((resolve, reject) => {
      const id = crypto.randomUUID();
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      // Detect legacy format: { onChunk?, onDone?, onError? } vs new format: { timeout?, stream? }
      const isLegacyFormat =
        options &&
        ("onChunk" in options || "onDone" in options || "onError" in options);
      const streamOptions = isLegacyFormat
        ? (options as StreamOptions)
        : (options as CallOptions | undefined)?.stream;
      const timeout = isLegacyFormat
        ? undefined
        : (options as CallOptions | undefined)?.timeout;

      // Apply the default timeout as a backstop for non-streaming calls
      // so a lost response rejects instead of hanging forever. An
      // explicit `timeout` (including 0 = disabled) always wins.
      const effectiveTimeout =
        timeout !== undefined
          ? timeout
          : streamOptions
            ? undefined
            : (this.options.defaultCallTimeout ?? DEFAULT_CALL_TIMEOUT_MS);

      if (effectiveTimeout) {
        timeoutId = setTimeout(() => {
          const pending = this._pendingCalls.get(id);
          this._pendingCalls.delete(id);
          const errorMessage = `RPC call to ${method} timed out after ${effectiveTimeout}ms`;
          pending?.stream?.onError?.(errorMessage);
          reject(new Error(errorMessage));
        }, effectiveTimeout);
      }

      this._pendingCalls.set(id, {
        reject: (e: Error) => {
          if (timeoutId) clearTimeout(timeoutId);
          reject(e);
        },
        resolve: (value: unknown) => {
          if (timeoutId) clearTimeout(timeoutId);
          resolve(value);
        },
        stream: streamOptions,
        // If the socket is open, send() below transmits synchronously;
        // otherwise the request is buffered until the next open event
        // (the "open" listener then marks it transmitted).
        transmitted: this.readyState === this.OPEN
      });

      const request: RPCRequest = {
        args,
        id,
        method,
        type: MessageType.RPC
      };

      this.send(JSON.stringify(request));
    });
  }
}

/**
 * Make an HTTP request to an Agent
 * @param opts Connection options
 * @param init Request initialization options
 * @returns Promise resolving to a Response
 */
export function agentFetch(opts: AgentClientFetchOptions, init?: RequestInit) {
  const agentNamespace = camelCaseToKebabCase(opts.agent);

  // If basePath is provided, use it directly; otherwise construct from agent/name
  // When basePath is set, room/party aren't used by PartySocket (basePath replaces the URL)
  if (opts.basePath) {
    return PartySocket.fetch(
      { basePath: opts.basePath, ...opts } as unknown as PartyFetchOptions,
      init
    );
  }

  return PartySocket.fetch(
    {
      party: agentNamespace,
      prefix: "agents",
      room: opts.name || "default",
      ...opts
    },
    init
  );
}
